import { $config } from '@client/core/atoms/config.js';
import { type AdType, Brand } from '@schibsted-nmp/advertising-shared';

export function sizeIsNativeAd(
  size: googletag.events.SlotRenderEndedEvent['size']
) {
  return Array.isArray(size) && size[0] === 0 && size[1] === 0;
}

export function eventIsNativeCpmAd(
  event: googletag.events.SlotRenderEndedEvent & {
    // TODO: check up on this property, as it seems it should come from elsewhere, e.g. `googletag.ResponseInformation`:
    creativeTemplateId?: number;
  }
) {
  const nativeCpmCreativeTemplateIds = [12399765];

  return Boolean(
    event.creativeTemplateId &&
      nativeCpmCreativeTemplateIds.includes(event.creativeTemplateId)
  );
}

type SlotSize = googletag.events.SlotRenderEndedEvent['size'];

function isSize(size: SlotSize, w: number, h: number): boolean {
  return Array.isArray(size) && size[0] === w && size[1] === h;
}
export function sizeIsFullScreenPortrait(size: SlotSize): boolean {
  return isSize(size, 640, 1136) || isSize(size, 1080, 1920);
}

export function sizeIsFullScreenLandscape(size: SlotSize): boolean {
  return isSize(size, 1920, 1080);
}

export function sizeIsFullScreenAd(size: SlotSize): boolean {
  return sizeIsFullScreenLandscape(size) || sizeIsFullScreenPortrait(size);
}

export function adTypeIsTakeover(type: AdType | undefined): boolean {
  return typeof type === 'string' && type.startsWith('takeover');
}

export function sizeIsAdnami(
  size: googletag.events.SlotRenderEndedEvent['size']
) {
  return Array.isArray(size) && size[0] === 1 && size[1] === 2;
}

export function sizeIsDbaDesktopIntermingle(
  size: googletag.events.SlotRenderEndedEvent['size']
) {
  if (!Array.isArray(size)) return false;

  return (
    (size[0] === 930 && size[1] === 180) ||
    (size[0] === 970 && size[1] === 250) ||
    (size[0] === 970 && size[1] === 188) ||
    sizeIsPrebid(size)
  );
}

export function sizeExceedsCenterContentWidth(
  size: googletag.events.SlotRenderEndedEvent['size']
) {
  if (!Array.isArray(size)) return false;

  const { brand } = $config.get() ?? {};
  if (sizeIsPrebid(size) && brand === Brand.Dba) return true;

  const CENTER_CONTENT_WIDTH_LIMIT = 640; // TODO: temp threshold, should be verified with design team
  return (size[0] ?? 0) > CENTER_CONTENT_WIDTH_LIMIT;
}

function sizeIsPrebid(size: googletag.events.SlotRenderEndedEvent['size']) {
  return size[0] === 1 && size[1] === 1;
}

export function sizeBlocketTop1Wallpaper(
  size: googletag.events.SlotRenderEndedEvent['size']
) {
  if (!Array.isArray(size)) return false;

  return size[0] === 1600 && size[1] === 900;
}

/**
 * Temporary function for PostIt ads so 3rd party partner can test
 * specific iFrame changes via creativeId (also works with GAM preview links)
 */
export function isPostItTestCreative(
  event: googletag.events.SlotRenderEndedEvent
): boolean {
  const POSTIT_TEST_CREATIVE_IDS: ReadonlyArray<number> = [138546808183];

  const creativeIdFromEvent =
    !event.isEmpty && event.creativeId != null ? event.creativeId : null;

  const creativeIdFromUrl = getCreativeIdFromUrl();

  const effectiveCreativeId = creativeIdFromEvent ?? creativeIdFromUrl;

  return (
    effectiveCreativeId != null &&
    POSTIT_TEST_CREATIVE_IDS.includes(effectiveCreativeId)
  );
}

function getCreativeIdFromUrl(): number | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const creativeParam = params.get('creativeId');

  if (!creativeParam) return null;

  const parsed = Number(creativeParam);
  return Number.isFinite(parsed) ? parsed : null;
}
